import { ContainerTypeLegacy } from './constant';

export type NavigationParams = {
  container_id?: string | null;
  container_type?: ContainerTypeLegacy | null;
  container_name?: string;
  account_id?: string | null;
  pellet_id?: string;
  cable_id?: string | number;
};

/**
 * Derives the URL search param key for the given container_type.
 * Returns null if the container type is not recognized.
 */
function getContainerSearchKey(
  containerType: ContainerTypeLegacy | null | undefined
): 'bin' | 'barge' | 'pile' | null {
  switch (containerType) {
    case ContainerTypeLegacy.bin:
      return 'bin';
    case ContainerTypeLegacy.barge:
      return 'barge';
    case ContainerTypeLegacy.pile:
      return 'pile';
    default:
      return null;
  }
}

/**
 * Maps NavigationParams fields to URL search param keys.
 * Only includes params that are explicitly set in the input.
 * Returns undefined for keys that should be cleared.
 *
 * Key design decisions:
 * - Uses 'field' in params checks to distinguish "not specified" from "explicitly set to undefined/null"
 * - When container selection is specified, ALL container keys (bin, barge, pile) are touched —
 *   inactive ones cleared, active one set
 * - container_name is NOT mapped to URL — it's for iOS bridge only
 * - The retainSearchParams middleware handles preserving params not in the payload
 * - Params NOT present in the returned object are automatically preserved by the root
 *   route's retainSearchParams(['bin', 'barge', 'pile', 'acct', 'pellet', 'cable'])
 *   middleware — callers don't need to re-pass existing URL values
 */
export function buildSearchParams(params: NavigationParams): Record<string, string | undefined> {
  const search: Record<string, string | undefined> = {};

  // Container selection: map container_id + container_type to the correct URL key.
  // Clear all other container keys (only one container type active at a time).
  if ('container_id' in params || 'container_type' in params) {
    const { container_id, container_type } = params;

    // Clear all container keys first
    search.bin = undefined;
    search.barge = undefined;
    search.pile = undefined;

    // Set the active container key
    if (container_id && container_type) {
      const key = getContainerSearchKey(container_type);
      if (key) {
        search[key] = container_id;
      }
    }
  }

  if ('account_id' in params) {
    search.acct = params.account_id || undefined;
  }

  if ('pellet_id' in params) {
    search.pellet = params.pellet_id || undefined;
  }

  if ('cable_id' in params) {
    search.cable = params.cable_id ? String(params.cable_id) : undefined;
  }

  return search;
}

/**
 * Explicitly clears all retained search params.
 * Pass this to `history.replace('/login', { search: CLEAR_ALL_SEARCH_PARAMS })`
 * to override the retainSearchParams middleware and produce a clean URL.
 *
 * The retainSearchParams middleware on __root.tsx preserves any key NOT present
 * in the search object. Setting keys to `undefined` tells the middleware to
 * DROP them rather than retain them from the previous URL.
 */
export const CLEAR_ALL_SEARCH_PARAMS: Record<string, undefined> = {
  bin: undefined,
  barge: undefined,
  pile: undefined,
  acct: undefined,
  pellet: undefined,
  cable: undefined,
};
